import { ENDED_EVENT_STATUSES } from '@barso/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { AreaGroup } from '../components/AreaList';
import { ContinueRegister } from '../components/ContinueRegister';
import { Alert, Tile, TileGroup } from '../components/ui';
import { STATUS_LABELS, period } from '../format';
import { KIND_ICONS, KIND_LABELS, ROLE_LABELS } from './orgLabels';
import { OpenRegisterCards } from './posStart';
import { bazaarApi, type BazaarEvent, type EventStatus, type Organization } from '../api/endpoints';
import { messageFor } from '../api/messages';
import { Icon, WelcomeArt } from '../components/icons';
import { loadOrganizations } from '../hooks/useOrganizations';

/**
 * Startseite nach der Anmeldung (eigene Organisationen, Basare, Schnellzugriffe). Eigene Datei (Welle 5): Die Startseite
 * für Gäste (`landing.tsx`) und die App-Startseite laden sonst das ganze Modul „orgs“ beim Start mit.
 */
/** Abgeschlossene Basare: nicht mehr „aktuell“. Wie in `orgs.tsx`, dort mit weiteren Verwendungen. */
const FINISHED: readonly EventStatus[] = ENDED_EVENT_STATUSES;

export function HomePage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<(BazaarEvent & { orgName: string })[] | null>(null);

  useEffect(() => {
    loadOrganizations().then(setOrgs, (caught: unknown) => {
      setError(messageFor(caught));
    });
  }, []);

  // Laufende Basare aller eigenen Organisationen (Abkürzung; Fehler bleiben still, die Kacheln unten bleiben)
  useEffect(() => {
    if (orgs === null || orgs.length === 0) return;
    const guard = { cancelled: false };
    Promise.all(
      orgs.slice(0, 5).map((org) =>
        bazaarApi.listEvents(org.id).then(
          (events) => events.map((event) => ({ ...event, orgName: org.name })),
          () => [],
        ),
      ),
    ).then(
      (lists) => {
        if (guard.cancelled) return;
        setCurrent(
          lists
            .flat()
            .filter((event) => !FINISHED.includes(event.status))
            .sort((a, b) => (a.startsAt ?? '9999').localeCompare(b.startsAt ?? '9999')),
        );
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, [orgs]);
  const manageableOrg = orgs?.find((org) => org.role === 'owner' || org.role === 'admin');

  return (
    <div className="stack stack--lg">
      <section className="home-hero" aria-labelledby="begruessung">
        <div className="home-hero__text">
          <h1 id="begruessung">Hallo {user?.firstName ?? ''}</h1>
          <p className="page-description">
            Von hier aus erreichen Sie Ihre Organisationen, Ihre Anmeldungen als Verkäufer und Ihre
            Einsätze als Helfer.
          </p>
        </div>
        <WelcomeArt />
      </section>
      <nav className="quick-actions" aria-label="Schnellzugriffe">
        {manageableOrg !== undefined && (
          <Link
            className="quick-action quick-action--primary"
            to={`/organisationen/${manageableOrg.id}/basar-anlegen`}
          >
            <Icon name="calendar" size={20} />
            Basar anlegen
          </Link>
        )}
        <Link className="quick-action" to="/meine-schichten">
          <Icon name="calendar" size={20} />
          Meine Schichten
        </Link>
        <Link className="quick-action" to="/meine-anmeldungen">
          <Icon name="tag" size={20} />
          Meine Anmeldungen
        </Link>
        <Link className="quick-action" to="/einsaetze">
          <Icon name="users" size={20} />
          Meine Einsätze
        </Link>
        <Link className="quick-action" to="/basare-entdecken">
          <Icon name="search" size={20} />
          Basare entdecken
        </Link>
      </nav>
      {current !== null && current.length > 0 && (
        <AreaGroup
          group={{
            key: 'aktuelle-basare-start',
            title: 'Aktuelle Basare',
            rows: current.slice(0, 4).map((event) => ({
              key: event.id,
              to: `/basare/${event.id}`,
              icon: 'calendar',
              title: event.title,
              // Termin zuerst: Bei schmaler Zeile kürzt die Auslassung den Namen der Organisation, nicht das Datum
              description: `${period(event.startsAt, event.endsAt)} · ${event.orgName}`,
              badge: STATUS_LABELS[event.status],
            })),
          }}
        />
      )}
      <ContinueRegister />
      <OpenRegisterCards />
      {error !== null && <Alert kind="error">{error}</Alert>}
      {orgs === null && error === null && <p role="status">Einen Moment bitte …</p>}
      {orgs !== null && orgs.length > 0 && (
        <section className="stack" aria-labelledby="meine-orgs">
          <h2 id="meine-orgs">Ihre Organisationen</h2>
          <TileGroup>
            {orgs.map((org) => (
              <Tile
                key={org.id}
                to={`/organisationen/${org.id}`}
                icon={KIND_ICONS[org.kind]}
                title={org.name}
                description={KIND_LABELS[org.kind]}
                badge={ROLE_LABELS[org.role]}
              />
            ))}
          </TileGroup>
        </section>
      )}

      <TileGroup title="Für mich">
        <Tile
          to="/profil"
          icon="user"
          title="Profil"
          description="Kontaktdaten, Sicherheit, Meine Daten"
        />
        <Tile
          to="/basar-anbieten"
          icon="home"
          title={orgs !== null && orgs.length > 0 ? 'Weitere Organisation' : 'Basar anbieten'}
          description="Als Verein, Kita oder Schule: Organisation anlegen und eigene Basare veranstalten"
        />
        {user?.platformRole === 'platform_admin' && (
          <Tile
            to="/plattform"
            icon="shield"
            title="Plattform-Verwaltung"
            description="Lizenzen, Organisationen, Nutzer"
          />
        )}
      </TileGroup>
    </div>
  );
}

/**
 * „Basar anbieten“: eigene Seite für alle, die als Verein, Kita oder Schule Basare veranstalten möchten. Die
 * Startseite zeigt dafür nur eine Kachel; Verkäufer und Helfer sehen das Formular so nicht ungefragt.
 */
